import React, { useEffect, useState } from 'react';
import { UserSalary } from '../../../types';
import { formatCurrency } from '../../../../../../../utils/functions';
import { FaBars, FaCog, FaPlus } from 'react-icons/fa';
import CreateNewSalary from './components/CreateNewSalary';
import CreateCurrentSalary from './components/CreateCurrentSalary';
import SalaryDetailsModal from './components/SalaryDetailsModal';
import { Button } from 'react-bootstrap';
import './Table.scss';


interface TableProps {
  data: UserSalary[];
  setData: React.Dispatch<React.SetStateAction<UserSalary[]>>;
}

const Table: React.FC<TableProps> = ({ data, setData }) => {
  const [showNewModal, setShowNewModal] = useState(false);
  const [showCurrentModal, setShowCurrentModal] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<{ id: number; username: string } | null>(null);
  const [currentTableData, setCurrentTableData] = useState<
    { user_id: number; username: string; lastname: string; total_payed: number; payment_count: number }[]
  >([]);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile width
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function firstLastName(string: string): string {
    const parts = string.split(' ');
    if (parts.length === 0) return '';
    const first = parts[0].trim();
    // Si es móvil, retornar solo la primera letra del primer apellido
    if (isMobile && first.length > 0) {
      return `${first.charAt(0)}.`;
    }
    return `${first}`;
  }

  // Group by user_id + lastname, sum payments and counts
  useEffect(() => {
    const grouped = Object.values(
      data.reduce((acc, user) => {
        if (user.user_id == null) return acc;
        const key = `${user.user_id}-${user.last_name?.trim()}`;

        if (!acc[key]) {
          acc[key] = {
            user_id: user.user_id,
            username: user.username?.trim() || `Usuario ${user.user_id}`,
            lastname: user.last_name?.trim() || '',
            total_payed: 0,
            payment_count: 0,
          };
        }

        acc[key].total_payed += user.total_payed;
        acc[key].payment_count += 1;

        return acc;
      }, {} as Record<
        string,
        {
          user_id: number;
          username: string;
          lastname: string;
          total_payed: number;
          payment_count: number;
        }
      >)
    );

    setCurrentTableData(grouped);
  }, [data]);

  return (
    <div className="custom-table-div">
      <div className="action-button-table-container">
        <Button variant="none" className="mp-button light" onClick={() => setShowNewModal(true)}>
          <FaPlus /> Crear nuevo salario
        </Button>
      </div>

      <div className="scroll-container-table">
        <table className="custom-table">
          <thead>
            <tr>
              <th className="no_pagination">Usuario</th>
              <th className="no_pagination">Promedio Pagado</th>
              <th className="no_pagination">{isMobile ? <FaCog /> : 'Acciones'}</th>
              <th className="no_pagination">Detalles</th>
            </tr>
          </thead>
          <tbody>
            {currentTableData.map(user => {
              const fullName = `${user.username} ${firstLastName(user.lastname)}`.trim();

              return (
                <tr key={`${user.user_id}-${user.lastname}`}>
                  <td>{fullName}</td>
                  <td>{formatCurrency(user.total_payed / user.payment_count)}</td>
                  <td>
                    <Button
                      variant="none"
                      className="table-button"
                      onClick={() => setShowCurrentModal(user.user_id)}
                    >
                      <FaPlus /> Agregar pago
                    </Button>
                  </td>
                  <td>
                    <Button
                      variant="none"
                      className="table-button"
                      style={isMobile ? { fontSize: 'small' } : undefined}
                      onClick={() =>
                        setShowDetailsModal({ id: user.user_id, username: fullName })
                      }
                    >
                      <FaBars style={{ marginRight: 3 }} /> Ver más
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateNewSalary
        show={showNewModal}
        handleClose={() => setShowNewModal(false)}
        setData={setData}
        data={data}
      />

      {showCurrentModal != null && (
        <CreateCurrentSalary
          show={true}
          handleClose={() => setShowCurrentModal(null)}
          setData={setData}
          userId={showCurrentModal}
          data={data}
        />
      )}

      {showDetailsModal != null && (
        <SalaryDetailsModal
          show={true}
          handleClose={() => setShowDetailsModal(null)}
          userId={showDetailsModal.id}
          username={showDetailsModal.username}
        />
      )}
    </div>
  );
};

export default Table;
